import { Group, Text, Accordion, Flex, Box, Select, Table } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { TbCalendar } from 'react-icons/tb';
import dayjs from '../../../../../../utils/dayjs';
import { useMemo, useState } from 'react';
import { BsQuestionCircle } from 'react-icons/bs';
import { IoIosArrowDown } from 'react-icons/io';
import {
  calculateFilerData,
  FUND_RAUM_KEYS,
  HEDGE_FUND_RAUM_OPTIONS,
  LIQUIDITY_FUND_RAUM_OPTIONS,
  type FundRaumKey
} from '../../data';

/**
 * `bare` drops the accordion this is normally folded into. In a window of its own the fold is a
 * second copy of the title, collapsed over the thing you opened the window to see.
 */
export default function FormPFDeadlineCalculator({ bare = false }: { bare?: boolean } = {}) {
  const [fiscalDate, setFiscalDate] = useState<string>('');
  const [selectedHedgeFundRaum, setSelectedHedgeFundRaum] = useState<FundRaumKey>(
    FUND_RAUM_KEYS.UNDER
  );
  const [selectedLiquidityFundRaum, setSelectedLiquidityFundRaum] = useState<FundRaumKey>(
    FUND_RAUM_KEYS.UNDER
  );

  const calculatedFilerData = useMemo(() => {
    if (!fiscalDate || !selectedHedgeFundRaum || !selectedLiquidityFundRaum) return [];

    return calculateFilerData(fiscalDate, selectedHedgeFundRaum, selectedLiquidityFundRaum);
  }, [selectedHedgeFundRaum, selectedLiquidityFundRaum, fiscalDate]);

  const rows = calculatedFilerData?.map(element => (
    <Table.Tr key={element.id} role="row" aria-label="form-pf-row">
      <Table.Td fz={14} fw={400} bd="1px solid #DEE2E6">
        {element.end_date}
      </Table.Td>
      <Table.Td fz={14} fw={400} bd="1px solid #DEE2E6" maw={140}>
        {element.due_date}
      </Table.Td>
      <Table.Td fz={14} fw={400} bd="1px solid #DEE2E6" maw={140}>
        {element.type}
      </Table.Td>
      <Table.Td fz={14} fw={400} bd="1px solid #DEE2E6">
        {element.period}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Accordion
      chevronPosition="right"
      variant="contained"
      radius="md"
      bg={bare ? 'transparent' : 'gray.0'}
      value={bare ? 'deadline-calculator' : undefined}
      styles={
        bare
          ? { item: { border: 'none', background: 'transparent' }, content: { padding: 0 } }
          : undefined
      }
    >
      <Accordion.Item value="deadline-calculator">
        {!bare && (
          <Accordion.Control>
            <Group wrap="nowrap">
              <Flex gap={8}>
                <BsQuestionCircle color="#228BE6" />
                <Text size="16px">
                  Not sure which type to choose? Use FormPF deadline calculator
                </Text>
              </Flex>
            </Group>
          </Accordion.Control>
        )}
        <Accordion.Panel>
          <Flex direction="column" w="100%" gap={16}>
            <Box>
              {/* The app's picker, not the browser's: the native input renders in the browser's
                  locale and its own chrome next to fields that are the app's. */}
              <DatePickerInput
                label="Most Recently Completed Fiscal Year End"
                placeholder="Select date"
                valueFormat="D MMM YYYY"
                rightSection={<TbCalendar size={16} />}
                value={fiscalDate ? dayjs(fiscalDate).toDate() : null}
                onChange={date => setFiscalDate(date ? dayjs(date).format('YYYY-MM-DD') : '')}
                name="fiscal_year_end"
                data-testid="fiscal_year_end"
                w="49%"
              />
            </Box>
            <Flex gap={8} justify="space-between">
              <Select
                label="Hedge Fund RAUM"
                w="100%"
                data-testid="hedge_fund_raum"
                value={selectedHedgeFundRaum}
                placeholder=""
                data={HEDGE_FUND_RAUM_OPTIONS}
                rightSection={<IoIosArrowDown />}
                onChange={value => setSelectedHedgeFundRaum(value as FundRaumKey)}
                styles={{
                  wrapper: {
                    background: '#F8F9FA'
                  },
                  input: {
                    background: '#F8F9FA'
                  }
                }}
              />

              <Select
                label="Liquidity Fund RAUM"
                w="100%"
                data-testid="liquidity_fund_raum"
                value={selectedLiquidityFundRaum}
                placeholder=""
                data={LIQUIDITY_FUND_RAUM_OPTIONS}
                rightSection={<IoIosArrowDown />}
                onChange={value => setSelectedLiquidityFundRaum(value as FundRaumKey)}
                styles={{
                  wrapper: {
                    background: '#F8F9FA'
                  },
                  input: {
                    background: '#F8F9FA'
                  }
                }}
              />
            </Flex>

            <Table bd="1px solid #DEE2E6" role="table" data-testid="form-pf-calculator-table">
              <Table.Thead>
                <Table.Tr bg="gray.2">
                  <Table.Th fz={14} fw={600} c="gray.7" bd="1px solid #DEE2E6">
                    Period End Date
                  </Table.Th>
                  <Table.Th fz={14} fw={600} c="gray.7" bd="1px solid #DEE2E6">
                    Submission Due Date
                  </Table.Th>
                  <Table.Th fz={14} fw={600} c="gray.7" bd="1px solid #DEE2E6">
                    Form PF Type
                  </Table.Th>
                  <Table.Th fz={14} fw={600} c="gray.7" bd="1px solid #DEE2E6">
                    Period
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{rows}</Table.Tbody>
            </Table>
          </Flex>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
